import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Stack,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { useTenant } from '../providers/TenantProvider.js';
import { useNavigate } from 'react-router-dom';
import { db, functions } from '../firebase.js';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';

type UserRole = 'owner' | 'manager' | 'coach' | 'staff';
type MembershipRole = UserRole | 'member';

interface User {
  id: string;
  uid: string;
  email: string;
  role: UserRole;
  roles: MembershipRole[];
  primaryRole: UserRole;
  firstName?: string;
  lastName?: string;
  serviceIds?: string[];
  createdAt: any;
  updatedAt: any;
}

export function UsersPage() {
  const navigate = useNavigate();
  const { tenantId, role } = useTenant();
  const [users, setUsers] = useState<User[]>([]);
  // const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  // const [servicesLoading, setServicesLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'staff' as UserRole,
    firstName: '',
    lastName: '',
    serviceIds: [] as string[],
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const canManageUsers = role === 'owner';

  // Load users and services
  useEffect(() => {
    if (!tenantId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        // setServicesLoading(true);

        // Load users
        const usersQuery = query(
          collection(db, 'tenants', tenantId, 'memberships'),
          where('roles', 'array-contains-any', ['owner', 'manager', 'staff', 'coach'])
        );
        const usersSnapshot = await getDocs(usersQuery);
        const usersData = usersSnapshot.docs
          .map((docSnap) => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              uid: data.uid || docSnap.id,
              ...data,
              role: data.primaryRole,
            } as User;
          })
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setUsers(usersData);

        // Load services for assignment (disabled - unused)
        // const servicesQuery = query(
        //   collection(db, 'services'),
        //   where('tenantId', '==', tenantId),
        //   where('isActive', '==', true),
        //   orderBy('name')
        // );
        // const servicesSnapshot = await getDocs(servicesQuery);
        // const servicesData: Service[] = [];
        // servicesSnapshot.forEach((docSnap) => {
        //   servicesData.push({ id: docSnap.id, ...docSnap.data() } as Service);
        // });
        // setServices(servicesData);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
        // setServicesLoading(false);
      }
    };

    loadData();
  }, [tenantId]);

  const handleCreateUser = async () => {
    if (!tenantId) {
      setError('Tenant ID not found');
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const addTenantMembership = httpsCallable(functions, 'addTenantMembership');
      const result = await addTenantMembership({
        email: formData.email,
        password: formData.password || undefined,
        roles: [formData.role],
        firstName: formData.firstName,
        lastName: formData.lastName,
        tenantId,
        serviceIds: formData.serviceIds,
      });

      const data = result.data as { success: boolean; userId: string; message?: string };

      if (data.success) {
        setSuccess('User created successfully');
        setCreateDialogOpen(false);
        setFormData({ email: '', password: '', role: 'staff', firstName: '', lastName: '', serviceIds: [] });
        
        // Reload users
        const usersQuery = query(
          collection(db, 'tenants', tenantId, 'memberships'),
          where('roles', 'array-contains-any', ['owner', 'manager', 'staff', 'coach'])
        );
        const usersSnapshot = await getDocs(usersQuery);
        const usersData = usersSnapshot.docs
          .map((docSnap) => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              uid: data.uid || docSnap.id,
              ...data,
              role: data.primaryRole,
            } as User;
          })
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setUsers(usersData);
      }
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.message || 'Failed to create user');
    }
  };

  const handleUpdateUserRole = async () => {
    if (!selectedUser || !tenantId) return;

    try {
      setError(null);
      setSuccess(null);

      const nextRoles: MembershipRole[] = selectedUser.roles?.includes('member')
        ? [formData.role, 'member']
        : [formData.role];
      const updateTenantMembership = httpsCallable(functions, 'updateTenantMembership');
      const result = await updateTenantMembership({
        tenantId,
        uid: selectedUser.uid || selectedUser.id,
        roles: nextRoles,
        firstName: formData.firstName,
        lastName: formData.lastName,
        serviceIds: formData.serviceIds,
      });

      const data = result.data as { success: boolean; message?: string };

      if (data.success) {
        setSuccess('User updated successfully');
        setEditDialogOpen(false);
        setSelectedUser(null);
        
        // Update local state
        setUsers(users.map(u => 
          u.id === selectedUser.id
            ? { ...u, role: formData.role, primaryRole: formData.role, roles: nextRoles, serviceIds: formData.serviceIds }
            : u
        ));
      }
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const removeTenantMembership = httpsCallable(functions, 'removeTenantMembership');
      const result = await removeTenantMembership({ tenantId, uid: userId });

      const data = result.data as { success: boolean; message?: string };

      if (data.success) {
        setSuccess('User deleted successfully');
        setUsers(users.filter(u => u.id !== userId));
      }
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Failed to delete user');
    }
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      role: user.role,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      serviceIds: user.serviceIds || [],
    });
    setEditDialogOpen(true);
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'manager':
        return 'warning';
      case 'coach':
        return 'info';
      case 'staff':
        return 'default';
      default:
        return 'default';
    }
  };

  if (!canManageUsers) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          You do not have permission to manage users. Only tenant owners can access this page.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Loading users...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage staff accounts and permissions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Add User
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Users Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.firstName || user.lastName
                        ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role.toUpperCase()}
                        size="small"
                        color={getRoleColor(user.role)}
                      />
                    </TableCell>
                    <TableCell>
                      {user.createdAt
                        ? new Date(user.createdAt.seconds * 1000).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/schedules/${user.id}`)}
                          title="Manage Schedule"
                        >
                          <CalendarIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(user)}
                          disabled={user.role === 'owner'}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={user.role === 'owner'}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, email: e.target.value })}
              helperText="User will use this email to sign in"
            />
            <TextField
              label="Password"
              type="password"
              required
              value={formData.password}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, password: e.target.value })}
              helperText="Minimum 6 characters"
            />
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, firstName: e.target.value })}
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, lastName: e.target.value })}
            />
            <TextField
              select
              label="Role"
              required
              value={formData.role}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, role: e.target.value as UserRole })}
              helperText="Select the user's permission level"
            >
              <MenuItem value="staff">Staff</MenuItem>
              <MenuItem value="coach">Coach</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={!formData.email || !formData.password || formData.password.length < 6}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User Role</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              value={formData.email}
              disabled
              helperText="Email cannot be changed"
            />
            <TextField
              select
              label="Role"
              required
              value={formData.role}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, role: e.target.value as UserRole })}
              helperText="Update the user's permission level"
            >
              <MenuItem value="staff">Staff</MenuItem>
              <MenuItem value="coach">Coach</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateUserRole}>
            Update Role
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
